import React, { lazy, useEffect, useState, type ComponentType, type FC } from 'react';
import {
  useSDMCartContext,
  useSDMCustomerContext,
  useSDMViewportContext,
  type ISDMContextsProps,
} from '@ldp/sdm-contexts';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/react';
import { useCommonContextProps, useContentfulPreview, useTrackPageView } from '@/hooks';
import { Spinner } from '@/components/shared';
import Footer from '@/components/footer';
import Masthead from '@/components/masthead';
import PageLayout from '@/components/page-layout/page-layout';
import { IViewResolverComponentProps } from './view-resolver.interface';
import { SdmProvince, VIEW_RESOLVER, ADA_CHATBOT, PAGE_ID } from '@/data/constants';

/** This describes how the federated module can be used in legacy apps */
export const ViewResolverComponent: FC<IViewResolverComponentProps & { enableSSR?: boolean }> = ({
  initialData,
  themeTokens,
  headerSection,
  ...rest
}) => {
  const { renderDatePicker, isContentfulPreview, selectedDateTime } = useContentfulPreview();
  const router = useRouter();

  const [ViewResolverSDM, setViewResolverSDM] = useState<ComponentType<any> | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // @ts-ignore
      setViewResolverSDM(lazy(() => import('ldpViewResolverSdm/Resolver')));
    }
  }, []);

  // Either text or promotionCodes is required for search page, redirect to home page if both are missing
  if (router.pathname === '/search' && !router.query?.text && !router.query?.promotionCodes) {
    router.push('/');
  }

  const { commonContextProps, locale, componentError } = useCommonContextProps();

  const sdmContextProps: ISDMContextsProps = {
    sdmCartProviderProps: {
      ...useSDMCartContext(),
    },
    sdmCustomerProviderProps: {
      ...useSDMCustomerContext(),
    },
    sdmRouterProps: { router },
    sdmViewportProviderProps: {
      ...useSDMViewportContext(),
    },
    provinceProviderProps: { children: null },
  };

  //analytics
  const { cartCount, isInitialCartCountUpdated, setPageId } = useSDMCartContext();
  const { pcOptimumAccount } = useSDMCustomerContext();
  const { pageId } = rest;

  useEffect(() => {
    setPageId(pageId);
  }, [pageId, setPageId]);

  useTrackPageView(cartCount, pageId, pcOptimumAccount!, isInitialCartCountUpdated);

  //Enable Ada Chatbot Get Help button on Contact us Page
  useEffect(() => {
    const getHelpButton = document.getElementById(ADA_CHATBOT.GET_HELP_BUTTON);
    if (getHelpButton) {
      if (pageId?.includes(PAGE_ID.CONTACT_US)) {
        //show Get Help button on contact us page
        getHelpButton.style.display = 'block';
      } else {
        getHelpButton.style.display = 'none';
      }
    }
  }, [pageId]);

  /** Capture Masthead Error in Sentry */
  useEffect(() => {
    if (componentError?.mastheadError) {
      Sentry.captureMessage(componentError.mastheadError);
    }
  }, [componentError]);

  return (
    <>
      {ViewResolverSDM ? (
        <ViewResolverSDM
          {...rest}
          hadExistingSentry={true}
          isPreview={isContentfulPreview}
          previewDatetime={selectedDateTime}
          commonContexts={commonContextProps}
          sdmContexts={sdmContextProps}
          queryConfig={{
            // The initial data will be used to populate the cache, could be SSR fetched
            initialData,
            // Setting staleTime to Infinity will prevent the data from being refetched
            staleTime: Infinity,
          }}
          themeTokens={themeTokens}
          PageLoadingFallback={() => <Spinner />}
          locale={locale}
          env={process.env.NEXT_PUBLIC_VIEW_RESOLVER_ENV}
        >
          <Masthead headerSection={headerSection ? headerSection : { region: SdmProvince.ON }} />
          <PageLayout typeId={initialData ? initialData?.layout?.typeId : VIEW_RESOLVER.PAGE_LAYOUT_SINGLE_COLUMN} />
          <Footer />
        </ViewResolverSDM>
      ) : (
        <Spinner />
      )}
      {isContentfulPreview ? renderDatePicker() : null}
    </>
  );
};

export default ViewResolverComponent;
